import React, { useState, useEffect, useRef } from 'react';
import { Student } from '../../types';
import { paymentsApi } from '../../services/feesApi';
import { studentApi } from '../../services/api';
import { toast } from 'react-toastify';
import './InvoiceCreator.css';

interface FeeItem {
  id: number;
  description: string;
  qty: string;
  rate: string;
}

interface InvoiceCreatorProps {
  onClose?: () => void;
  inline?: boolean;
  editInvoiceId?: number | null;
  onEditComplete?: () => void;
}

const InvoiceCreator: React.FC<InvoiceCreatorProps> = ({ onClose, inline = false, editInvoiceId, onEditComplete }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [studentName, setStudentName] = useState('');
  const [fathersName, setFathersName] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [emiratesId, setEmiratesId] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [transactionNumber, setTransactionNumber] = useState('');
  const [transactionDate, setTransactionDate] = useState('');
  const [bankName, setBankName] = useState('');
  const [trnNumber, setTrnNumber] = useState('');
  const [vatRate, setVatRate] = useState('0.00');
  const [other, setOther] = useState('');
  const [items, setItems] = useState<FeeItem[]>([
    { id: 1, description: '', qty: '1', rate: '' }
  ]);
  const [loading, setLoading] = useState(false);
  const [generatingNumber, setGeneratingNumber] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [isStudentDropdownOpen, setIsStudentDropdownOpen] = useState(false);
  const studentDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchStudents();
    if (editInvoiceId) {
      loadInvoiceForEdit(editInvoiceId);
    } else {
      generateInvoiceNumber();
    }
  }, [editInvoiceId]);

  const loadInvoiceForEdit = async (id: number) => {
    try {
      setLoading(true);
      const response = await paymentsApi.getInvoiceData(id);
      const data = response.data;
      setIsEditMode(true);
      setInvoiceNumber(data.invoiceNumber);
      setInvoiceDate(data.invoiceDate ? new Date(data.invoiceDate).toISOString().split('T')[0] : '');
      setStudentName(data.studentName || '');
      setFathersName(data.fathersName || '');
      setGradeLevel(data.gradeLevel || '');
      setEmiratesId(data.emiratesId || '');
      setPaymentMethod(data.paymentMethod || 'CASH');
      setTransactionNumber(data.transactionNumber || '');
      setTransactionDate(data.transactionDate ? new Date(data.transactionDate).toISOString().split('T')[0] : '');
      setBankName(data.bankName || '');
      setTrnNumber(data.trnNumber || '');
      setVatRate(String(data.vatRate || '0.00'));
      setOther(String(data.other || ''));
      setItems(data.items.map((item: any, idx: number) => ({
        id: idx + 1,
        description: item.description,
        qty: String(item.qty !== undefined ? item.qty : 1),
        rate: String(item.rate !== undefined ? item.rate : item.amount)
      })));
    } catch (error) {
      console.error('Error loading invoice for edit:', error);
      toast.error('Failed to load invoice data');
    } finally {
      setLoading(false);
    }
  };

  // Close student dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (studentDropdownRef.current && !studentDropdownRef.current.contains(e.target as Node)) {
        setIsStudentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredStudents = students.filter(student => {
    const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
    const search = studentSearch.toLowerCase().trim();
    return !search || fullName.includes(search) || (student.emirates_id || '').toLowerCase().includes(search);
  });

  const fetchStudents = async () => {
    try {
      const response = await studentApi.getAll({ active: true, limit: 1000 });
      setStudents(response.data.students);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const generateInvoiceNumber = async () => {
    try {
      setGeneratingNumber(true);
      const response = await paymentsApi.generateVoucherNumber();
      setInvoiceNumber(response.data.receiptVoucherNumber);
    } catch (error) {
      console.error('Error generating invoice number:', error);
      // Fallback: generate locally
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0].replace(/-/g, '');
      const rand = String(Math.floor(Math.random() * 9999)).padStart(4, '0');
      setInvoiceNumber(`INV-${dateStr}-${rand}`);
    } finally {
      setGeneratingNumber(false);
    }
  };

  const handleStudentChange = (studentId: string) => {
    setSelectedStudentId(studentId);

    if (!studentId) {
      setStudentName('');
      setFathersName('');
      setGradeLevel('');
      setEmiratesId('');
      return;
    }

    const student = students.find(s => s.id === parseInt(studentId));
    if (student) {
      setStudentName(`${student.first_name} ${student.last_name}`);
      setFathersName(student.fathers_name || '');
      setGradeLevel(student.grade_level || '');
      setEmiratesId(student.emirates_id || '');
    }
  };

  const addItem = () => {
    const nextId = Math.max(...items.map(i => i.id), 0) + 1;
    setItems([...items, { id: nextId, description: '', qty: '1', rate: '' }]);
  };

  const removeItem = (id: number) => {
    if (items.length <= 1) return;
    setItems(items.filter(item => item.id !== id));
  };

  const updateItem = (id: number, field: 'description' | 'qty' | 'rate', value: string) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const getItemTotal = (item: FeeItem) => {
    return (parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0);
  };

  const getTotal = () => {
    return items.reduce((sum, item) => sum + getItemTotal(item), 0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    if (!invoiceNumber.trim()) {
      toast.error('Invoice number is required');
      return;
    }
    if (!invoiceDate) {
      toast.error('Invoice date is required');
      return;
    }
    if (!studentName.trim()) {
      toast.error('Student name is required');
      return;
    }

    // Check if any item has description but no valid rate
    const itemWithMissingRate = items.find(item => item.description.trim() && (!item.rate || parseFloat(item.rate) <= 0));
    if (itemWithMissingRate) {
      toast.error('Rate is mandatory for each fee item with a description');
      return;
    }

    const validItems = items.filter(item => item.description.trim() && parseFloat(item.rate) > 0);
    if (validItems.length === 0) {
      toast.error('At least one fee item with description and rate is required');
      return;
    }

    try {
      setLoading(true);

      const invoiceData: any = {
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate,
        studentName: studentName.trim(),
        fathersName: fathersName.trim() || undefined,
        gradeLevel: gradeLevel.trim() || undefined,
        emiratesId: emiratesId.trim() || undefined,
        items: validItems.map(item => ({
          description: item.description.trim(),
          qty: parseFloat(item.qty) || 1,
          rate: parseFloat(item.rate),
          amount: (parseFloat(item.qty) || 1) * parseFloat(item.rate)
        })),
        paymentMethod,
        trnNumber: trnNumber.trim() || undefined,
        vatRate: parseFloat(vatRate) || 0,
        other: parseFloat(other) || 0
      };

      if (paymentMethod !== 'CASH') {
        if (transactionNumber.trim()) invoiceData.transactionNumber = transactionNumber.trim();
        if (transactionDate) invoiceData.transactionDate = transactionDate;
        if (bankName.trim()) invoiceData.bankName = bankName.trim();
      }

      if (isEditMode && editInvoiceId) {
        await paymentsApi.updateInvoice(editInvoiceId, invoiceData);
        toast.success('Invoice updated and downloaded successfully!');
        if (onEditComplete) onEditComplete();
        if (onClose) onClose();
        return;
      }

      await paymentsApi.generateInvoice(invoiceData);
      toast.success('Invoice generated and downloaded successfully!');
      if (inline) {
        // Reset form for next invoice
        setSelectedStudentId('');
        setStudentName('');
        setFathersName('');
        setGradeLevel('');
        setEmiratesId('');
        setStudentSearch('');
        setItems([{ id: 1, description: '', qty: '1', rate: '' }]);
        setPaymentMethod('CASH');
        setTransactionNumber('');
        setTransactionDate('');
        setBankName('');
        setTrnNumber('');
        setVatRate('0.00');
        setOther('');
        generateInvoiceNumber();
      } else if (onClose) {
        onClose();
      }

    } catch (error: any) {
      console.error('Error generating invoice:', error);
      toast.error('Failed to generate invoice');
    } finally {
      setLoading(false);
    }
  };

  const formContent = (
        <form onSubmit={handleSubmit} className="invoice-creator-body">
          {/* Invoice Info */}
          <div className="invoice-section">
            <h3>Invoice Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="invoiceNumber">Invoice Number <span className="required">*</span></label>
                <div className="voucher-input-group">
                  <input
                    type="text"
                    id="invoiceNumber"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="INV-YYYYMMDD-XXXX"
                    disabled={loading || generatingNumber}
                  />
                  <button
                    type="button"
                    className="generate-voucher-btn"
                    onClick={generateInvoiceNumber}
                    disabled={loading || generatingNumber}
                    title="Generate new invoice number"
                  >
                    {generatingNumber ? '...' : 'New'}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="invoiceDate">Invoice Date <span className="required">*</span></label>
                <input
                  type="date"
                  id="invoiceDate"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Student Info */}
          <div className="invoice-section">
            <h3>Student Details</h3>
            <div className="form-group" ref={studentDropdownRef}>
              <label>Select Student</label>
              <div className="student-search-dropdown">
                <input
                  type="text"
                  className="student-search-input"
                  value={isStudentDropdownOpen ? studentSearch : (selectedStudentId ? students.find(s => s.id === parseInt(selectedStudentId))?.first_name + ' ' + students.find(s => s.id === parseInt(selectedStudentId))?.last_name : studentSearch)}
                  onChange={(e) => {
                    setStudentSearch(e.target.value);
                    setIsStudentDropdownOpen(true);
                    if (!e.target.value) {
                      handleStudentChange('');
                    }
                  }}
                  onFocus={() => {
                    setIsStudentDropdownOpen(true);
                    if (selectedStudentId) {
                      setStudentSearch('');
                    }
                  }}
                  placeholder="Type to search students..."
                  disabled={loading}
                />
                {selectedStudentId && !isStudentDropdownOpen && (
                  <button
                    type="button"
                    className="clear-student-btn"
                    onClick={() => {
                      handleStudentChange('');
                      setStudentSearch('');
                    }}
                  >
                    ×
                  </button>
                )}
                {isStudentDropdownOpen && (
                  <div className="student-dropdown-list">
                    {filteredStudents.length === 0 ? (
                      <div className="student-dropdown-empty">No students found</div>
                    ) : (
                      filteredStudents.map(student => (
                        <div
                          key={student.id}
                          className={`student-dropdown-item ${selectedStudentId === String(student.id) ? 'selected' : ''}`}
                          onClick={() => {
                            handleStudentChange(String(student.id));
                            setStudentSearch('');
                            setIsStudentDropdownOpen(false);
                          }}
                        >
                          <span className="student-dropdown-name">{student.first_name} {student.last_name}</span>
                          {student.grade_level && <span className="student-dropdown-grade">{student.grade_level}</span>}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="studentName">Student Name <span className="required">*</span></label>
                <input
                  type="text"
                  id="studentName"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder="Student full name"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="fathersName">Parent Name</label>
                <input
                  type="text"
                  id="fathersName"
                  value={fathersName}
                  onChange={(e) => setFathersName(e.target.value)}
                  placeholder="Parent / Father's name"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="gradeLevel">Class / Grade</label>
                <input
                  type="text"
                  id="gradeLevel"
                  value={gradeLevel}
                  onChange={(e) => setGradeLevel(e.target.value)}
                  placeholder="Class or grade level"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="emiratesId">Emirates ID No</label>
                <input
                  type="text"
                  id="emiratesId"
                  value={emiratesId}
                  onChange={(e) => setEmiratesId(e.target.value)}
                  placeholder="Emirates ID number"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Fee Items */}
          <div className="invoice-section">
            <div className="section-header-row">
              <h3>Fee Items</h3>
              <button
                type="button"
                className="btn btn-sm btn-add-item"
                onClick={addItem}
                disabled={loading}
              >
                + Add Item
              </button>
            </div>

            <div className="fee-items-table">
              <div className="fee-items-header">
                <span className="col-sno">#</span>
                <span className="col-desc">Description</span>
                <span className="col-qty">Qty</span>
                <span className="col-rate">Rate (AED)</span>
                <span className="col-amount">Total (AED)</span>
                <span className="col-action"></span>
              </div>
              {items.map((item, index) => (
                <div key={item.id} className="fee-item-row">
                  <span className="col-sno">{index + 1}</span>
                  <input
                    className="col-desc"
                    type="text"
                    value={item.description}
                    onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                    placeholder="Fee description"
                    disabled={loading}
                  />
                  <input
                    className="col-qty"
                    type="number"
                    value={item.qty}
                    onChange={(e) => updateItem(item.id, 'qty', e.target.value)}
                    placeholder="1"
                    step="0.001"
                    min="0"
                    disabled={loading}
                  />
                  <input
                    className="col-rate"
                    type="number"
                    value={item.rate}
                    onChange={(e) => updateItem(item.id, 'rate', e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    disabled={loading}
                  />
                  <span className="col-amount" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 8px', fontWeight: 600 }}>
                    {formatCurrency(getItemTotal(item))}
                  </span>
                  <button
                    type="button"
                    className="col-action remove-item-btn"
                    onClick={() => removeItem(item.id)}
                    disabled={loading || items.length <= 1}
                    title="Remove item"
                  >
                    ×
                  </button>
                </div>
              ))}
              <div className="fee-items-total">
                <span className="total-label">Subtotal:</span>
                <span className="total-amount">AED {formatCurrency(getTotal())}</span>
              </div>
            </div>

            {/* VAT Rate */}
            <div className="form-row" style={{ marginTop: '10px' }}>
              <div className="form-group">
                <label htmlFor="vatRate">VAT Rate (%)</label>
                <input
                  type="number"
                  id="vatRate"
                  value={vatRate}
                  onChange={(e) => setVatRate(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  max="100"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="other">Other (AED)</label>
                <input
                  type="number"
                  id="other"
                  value={other}
                  onChange={(e) => setOther(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="invoice-section">
            <h3>Payment Method</h3>
            <div className="form-group">
              <label htmlFor="paymentMethod">Method</label>
              <select
                id="paymentMethod"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                disabled={loading}
              >
                <option value="CASH">Cash</option>
                <option value="CARD">Card</option>
                <option value="ONLINE PAYMENT">Online Payment</option>
                <option value="CHEQUE">Cheque</option>
                <option value="BANK TRANSFER">Bank Transfer</option>
              </select>
            </div>

            {paymentMethod !== 'CASH' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="transactionNumber">Transaction No.</label>
                    <input
                      type="text"
                      id="transactionNumber"
                      value={transactionNumber}
                      onChange={(e) => setTransactionNumber(e.target.value)}
                      placeholder="Transaction reference"
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="transactionDate">Transaction Date</label>
                    <input
                      type="date"
                      id="transactionDate"
                      value={transactionDate}
                      onChange={(e) => setTransactionDate(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="bankName">Bank Name</label>
                  <input
                    type="text"
                    id="bankName"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="Bank name"
                    disabled={loading}
                  />
                </div>
              </>
            )}
          </div>

          {/* TRN Number — always shown on invoice */}
          <div className="invoice-section">
            <div className="form-group">
              <label htmlFor="trnNumber">TRN Number</label>
              <input
                type="text"
                id="trnNumber"
                value={trnNumber}
                onChange={(e) => setTrnNumber(e.target.value)}
                placeholder="e.g. 104586814600003 (leave blank to use default)"
                disabled={loading}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="invoice-actions">
            {!inline && onClose && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || generatingNumber}
            >
              {loading ? (isEditMode ? 'Updating...' : 'Generating...') : (isEditMode ? 'Update Invoice PDF' : 'Generate Invoice PDF')}
            </button>
          </div>
        </form>
  );

  if (inline) {
    return (
      <div className="invoice-creator invoice-creator-inline">
        <div className="invoice-creator-header">
          <h2>{isEditMode ? 'Edit Receipt Invoice' : 'Create Receipt Invoice'}</h2>
        </div>
        {formContent}
      </div>
    );
  }

  return (
    <div className="invoice-creator-overlay" onClick={onClose}>
      <div className="invoice-creator" onClick={(e) => e.stopPropagation()}>
        <div className="invoice-creator-header">
          <h2>{isEditMode ? 'Edit Receipt Invoice' : 'Create Receipt Invoice'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        {formContent}
      </div>
    </div>
  );
};

export default InvoiceCreator;
